/**
 * Product image upload (TO-416 product-admin-ui REQ-005): a client pre-check and
 * the four-step file-service sequence. Steps are injected so the order and the
 * stop-at-first-failure rule are unit-testable.
 */

/** 5 MiB — a UI rule; file-service accepts larger files than a product image needs. */
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']

export interface UploadSteps {
	requestUrl: (body: {
		fileName: string
		contentType: string
		sizeBytes: number
		entityType: string
		entityId: string
	}) => Promise<{ fileId: string; uploadUrl: string }>
	putBytes: (uploadUrl: string, file: File) => Promise<void>
	confirm: (fileId: string) => Promise<void>
	attach: (fileId: string) => Promise<void>
}

/** The pre-check's error key, or null when the file may be uploaded. */
export const checkImageFile = (file: File): string | null => {
	if (!IMAGE_TYPES.includes(file.type)) return 'image.errors.type'
	if (file.size < 1 || file.size > IMAGE_MAX_BYTES) return 'image.errors.size'
	return null
}

/** Upload URL → PUT bytes → confirm → attach to the product. A failing step stops the rest. */
export const uploadProductImage = async (file: File, productId: string, steps: UploadSteps): Promise<string> => {
	const { fileId, uploadUrl } = await steps.requestUrl({
		fileName: file.name,
		contentType: file.type,
		sizeBytes: file.size,
		entityType: 'product',
		entityId: productId,
	})
	await steps.putBytes(uploadUrl, file)
	await steps.confirm(fileId)
	await steps.attach(fileId)
	return fileId
}
